import { useState } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import type { TimeFilterType } from '../types';
import { FilterChip } from '../FilterChip/FilterChip';

export const TIME_OPTIONS: { value: TimeFilterType; label: string }[] = [
  { value: 'past-day', label: 'Past day' },
  { value: 'past-week', label: 'Past week' },
  { value: 'past-month', label: 'Past month' },
  { value: 'past-year', label: 'Past year' },
  { value: 'all', label: 'All time' },
];

interface TimeFilterProps {
  activeTime: TimeFilterType;
  onTimeChange: (t: TimeFilterType) => void;
}

export const TimeFilter = ({
  activeTime,
  onTimeChange,
}: Readonly<TimeFilterProps>) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const timeActive = activeTime !== 'all';
  const timeLabel =
    TIME_OPTIONS.find(o => o.value === activeTime)?.label ?? 'Time';

  return (
    <>
      <FilterChip
        label={timeActive ? timeLabel : 'Time'}
        icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
        active={timeActive}
        tooltip={
          timeActive
            ? `Filtering by ${timeLabel} — click to change`
            : 'Filter by time range'
        }
        onClick={e => setAnchor(e.currentTarget)}
        width={140}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { borderRadius: '15px' },
          },
        }}
      >
        <Box sx={{ minWidth: 200 }}>
          {TIME_OPTIONS.map(opt => (
            <MenuItem
              key={opt.value}
              selected={activeTime === opt.value}
              onClick={() => {
                onTimeChange(opt.value);
                setAnchor(null);
              }}
              disableRipple
              sx={{
                px: 2,
                py: 1.25,
                '&:hover': {
                  backgroundColor: 'rgba(0, 120, 214, 0.08)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 120, 214, 0.08)',
                  },
                },
              }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </>
  );
};
